import React, { useEffect } from "react";
import logo from "../../assets/logo2.png";
import { useTranslation } from "react-i18next";
import { indexStore } from "../../store/indexStore";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "../ui/sheet";
import { Menu, Home, Mail, X, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";

import Spacer from "../Spacer";
import i18n from "../../i18n";

function HeaderPage() {
  const { t } = useTranslation();
  const { lang, changeLang } = indexStore();
  const linkClassName = "text-base md:text-lg capitalize font-medium transition-colors duration-300 hover:text-primary relative group";

  useEffect(() => {
    const handleLanguageChange = () => {
      i18n.changeLanguage(lang);
      document.documentElement.dir = i18n.dir();
    };

    if (lang) {
      handleLanguageChange();
    } else {
      changeLang(localStorage.getItem("Lang") || "ar");
    }
  }, [lang, changeLang]);

  const toggleLanguage = () => {
    changeLang(lang === "ar" ? "en" : "ar");
  };

  return (
    <header className="sticky top-0 z-50 bg-background/20 backdrop-blur-md   shadow-sm">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-3 items-center py-3 md:py-4">
          {/* Logo */}
          <a href="/" className="flex items-center">
            <img
              width={150}
              height={150}
              src={logo}
              alt="Kreish Restaurant"
              className="h-20 md:h-24 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex gap-6 lg:gap-8 items-center justify-center">
            <a className={linkClassName} href="/">
              {t("pages.home")}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </a>

            <a className={linkClassName} href="/contact">
              {t("pages.contact")}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </a>
          </nav>

          {/* Desktop Language & Mobile Menu */}
          <div className="hidden md:flex items-center justify-end gap-3">
            <LanguageButton lang={lang} onToggle={toggleLanguage} />
          </div>

          {/* Mobile: Spacer */}
          <Spacer className="md:hidden" flex={true} />

          {/* Mobile: Language & Menu */}
          <div className="md:hidden flex items-center gap-3 justify-end">
            <LanguageButton lang={lang} onToggle={toggleLanguage} />

            <MobileMenuSheet t={t} lang={lang} />
          </div>
        </div>
      </div>
    </header>
  );
}

export default HeaderPage;

// Language Button Component
const LanguageButton = ({
  lang,
  onToggle,
}: {
  lang: string;
  onToggle: () => void;
}) => (
  <motion.button
    onClick={onToggle}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    className="flex items-center gap-2 rounded-lg p-2 md:p-2.5 hover:bg-primary/10 border border-border hover:border-primary transition-all duration-300 group"
    title={lang === "ar" ? "Switch to English" : "التبديل إلى العربية"}
  >
    {lang === "ar" ? (
      <>
        <span className="font-semibold text-sm md:text-base group-hover:text-primary transition-colors">ع</span>
      </>
    ) : (
      <>
        <span className="font-semibold text-sm md:text-base group-hover:text-primary transition-colors">EN</span>
      </>
    )}
  </motion.button>
);

// Mobile Menu Sheet Component
const MobileMenuSheet = ({ t, lang }: { t: any; lang: string }) => {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="p-2 rounded-lg hover:bg-muted transition-colors"
        >
          <Menu className="w-6 h-6" />
        </motion.button>
      </SheetTrigger>

      <SheetContent
        hiddenCloseButton={true}
        side={`${lang === "ar" ? "left" : "right"}`}
        className="w-[320px] sm:w-[380px] p-0 bg-gradient-to-br from-background via-background to-muted/30"
      >
        {/* Header with gradient */}
        <div className="relative bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 p-6 border-b border-border/50">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-primary/5"></div>
          <div className="relative z-10 flex items-center justify-between">
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary/90 bg-clip-text text-transparent">
              {t("pages.menu")}
            </h2>
            <SheetClose asChild>
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 rounded-lg hover:bg-primary/10 transition-colors"
              >
                <X className="w-5 h-5 text-foreground" />
              </motion.button>
            </SheetClose>
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="flex flex-col p-4 gap-2">
          {[
            { href: "/", icon: Home, label: t("pages.home") },
            { href: "/contact", icon: Mail, label: t("pages.contact") },
            { href: "/delete-account", icon: AlertCircle, label: t("pages.deleteAccountGuide") },
          ].map((item, index) => (
            <motion.a
              key={item.href}
              initial={{ opacity: 0, x: lang === "ar" ? 20 : -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{ x: lang === "ar" ? -8 : 8, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              href={item.href}
              className="group flex items-center gap-4 p-4 rounded-xl bg-card hover:bg-gradient-to-r hover:from-primary/10 hover:to-primary/5 border-2 border-transparent hover:border-primary/30 transition-all duration-300 shadow-sm hover:shadow-md"
            >
              <div className="flex-shrink-0 p-2.5 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                <item.icon className="w-5 h-5 text-primary" />
              </div>
              <span className="flex-1 text-base md:text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                {item.label}
              </span>
              <motion.div
                initial={{ opacity: 0, x: lang === "ar" ? 10 : -10 }}
                whileHover={{ opacity: 1, x: 0 }}
                className="text-primary"
              >
                <svg
                  className={`w-5 h-5 ${lang === "ar" ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </motion.div>
            </motion.a>
          ))}
        </nav>
      </SheetContent>
    </Sheet>
  );
};
